import { useState } from 'react';
import { CASE_LINKS_MAX, DOCUMENT_ACCEPT, DOCUMENT_MAX_BYTES, DOCUMENT_MAX_FILES, DOCUMENT_TYPES_LABEL, caseLinkProblem, documentFileProblem, normalizeCaseLink } from '@pqrs/contracts';
import { Alert, AlertDescription, AlertTitle } from '@xiltrion/ui/alert';
import { CargaArchivos } from '@xiltrion/ui/bloques/carga-archivos';
import { Etiquetas } from '@xiltrion/ui/bloques/selectores';
import { Icono } from '@xiltrion/ui/icono';
import { formatBytes } from '@xiltrion/ui/use-file-upload';
import { notify } from './lib';

export interface RejectedFile { name: string; reason: string }

/**
 * Zona de adjuntos de un expediente. La zona del paquete valida extensión, tamaño y cantidad; aquí además se lee el
 * comienzo de cada archivo (la misma regla que aplica el API al guardar), para que un archivo vacío o renombrado se
 * rechace al elegirlo y no después de enviar.
 */
export function CaseFilesDrop({ archivos, onArchivosChange, deshabilitado, titulo, textoAyuda, maxArchivos = DOCUMENT_MAX_FILES, 'aria-label': ariaLabel }: {
  archivos: File[];
  onArchivosChange: (files: File[]) => void;
  deshabilitado?: boolean;
  titulo?: string;
  textoAyuda?: string;
  /** Cuántos archivos admite la zona (1 = un solo archivo). */
  maxArchivos?: number;
  'aria-label': string;
}) {
  const [rejected, setRejected] = useState<RejectedFile[]>([]);
  const change = async (list: File[]) => {
    const checked = await Promise.all(list.map(async (f) => ({ f, problem: archivos.includes(f) ? null : await documentFileProblem(f) })));
    setRejected(checked.filter((c) => c.problem).map((c) => ({ name: c.f.name, reason: c.problem! })));
    onArchivosChange(checked.filter((c) => !c.problem).map((c) => c.f));
  };
  return (
    <div className="flex flex-col gap-3">
      <CargaArchivos archivos={archivos} onArchivosChange={(l) => void change(l)} multiple={maxArchivos > 1} deshabilitado={deshabilitado} accept={DOCUMENT_ACCEPT} maxTamano={DOCUMENT_MAX_BYTES} maxArchivos={maxArchivos}
        titulo={titulo} textoAyuda={textoAyuda ?? `${DOCUMENT_TYPES_LABEL} · hasta ${formatBytes(DOCUMENT_MAX_BYTES)} cada uno · máximo ${DOCUMENT_MAX_FILES} archivos`} aria-label={ariaLabel} />
      <RejectedFilesAlert files={rejected} title={rejected.length === 1 ? 'Un archivo no se agregó' : `${rejected.length} archivos no se agregaron`} />
    </div>
  );
}

/**
 * Anexos por enlace (Drive, OneDrive, WeTransfer…): lo que pesa más de lo permitido o ya está en una nube. Cada
 * enlace es una ficha; uno sin https:// se completa y uno que no es una dirección web queda en rojo con el motivo.
 */
export function CaseLinksInput({ id, value, onChange, max = CASE_LINKS_MAX, disabled, 'aria-describedby': describedBy }: {
  id: string;
  value: string[];
  onChange: (links: string[]) => void;
  max?: number;
  disabled?: boolean;
  'aria-describedby'?: string;
}) {
  return (
    <Etiquetas id={id} value={value} onChange={onChange} separador=" " max={max} disabled={disabled} aria-describedby={describedBy} className="w-full"
      placeholder="Pega el enlace y presiona Enter" transformar={normalizeCaseLink}
      validar={(t) => { const problem = caseLinkProblem(t); if (problem) notify('warn', 'Enlace no válido', problem); return !problem; }} />
  );
}

/** Lista de archivos rechazados con su motivo. */
export function RejectedFilesAlert({ files, title }: { files: RejectedFile[]; title: string }) {
  if (!files.length) return null;
  return (
    <Alert variant="destructive">
      <Icono nombre="mdi:close-circle-outline" />
      <AlertTitle>{title}</AlertTitle>
      <AlertDescription>
        <ul className="list-disc pl-5 m-0">{files.map((r, i) => <li key={`${r.name}-${i}`}><span className="font-medium">{r.name}</span>: {r.reason}</li>)}</ul>
      </AlertDescription>
    </Alert>
  );
}
